/**
 * Deciding when two entries are the same thing.
 *
 * People buy milk on Tuesday and again on Friday, dictate it once and type it
 * once, and end up with "Amul Milk 1L" and "milk". Those are one product and
 * two cartons: they need separate expiry dates, so they must stay separate
 * ITEMS — but they should be shown together, or the list becomes a wall and
 * "do I already have this?" gets the wrong answer.
 *
 * Deliberately dumb, and deliberately loose. This is string normalisation, not
 * understanding. An AI pass could do better and is the obvious next step — this
 * module is the seam it would slot into, because everything else depends on
 * `itemKey` and nothing depends on how it is computed.
 *
 * Over-matching costs a glance. Under-matching costs a duplicate purchase,
 * which is the thing the product exists to prevent.
 */
/** Words that carry no identity — they differ BETWEEN two of the same thing. */
const NOISE = new Set([
    "the",
    "a",
    "an",
    "of",
    "and",
    "packet",
    "packets",
    "pack",
    "pkt",
    "bottle",
    "bottles",
    "box",
    "tin",
    "can",
    "jar",
    "bag",
    "pouch",
    "piece",
    "pieces",
    "pcs",
    "fresh",
    "new",
]);
/** Quantities and units. "1L" is what differs between two identical cartons. */
const MEASURE = /\b\d+(\.\d+)?\s*(g|gm|gms|kg|ml|l|ltr|litre|liter|liters|litres|n|no|nos|pc|pcs|dozen)\b/g;
/**
 * A comparable key for an item name.
 *
 * "Amul Milk 1L", "amul milk", "2 packets of Amul milk" all collapse to
 * "amul milk".
 */
export function itemKey(name) {
    return (name
        .toLowerCase()
        // \p{M} keeps combining marks: without it "दूध" loses its vowel sign and
        // becomes "द ध", which matches nothing and breaks Devanagari entirely.
        .replace(/[^\p{L}\p{M}\p{N}\s]/gu, " ")
        .replace(MEASURE, " ")
        // Leading counts: "2 milk" is still milk.
        .replace(/\b\d+\b/g, " ")
        .split(/\s+/)
        .filter((w) => w.length > 0 && !NOISE.has(w))
        .join(" ")
        .trim());
}
/**
 * Do these two names refer to the same thing?
 *
 * Equal keys, or one key's words being a SUBSET of the other's.
 *
 * Subset, not "shares a word", and the difference is the whole rule. Sharing a
 * word makes "Amul Milk" match "Amul Butter" — they share a brand, which is
 * exactly the word that carries no product identity. Subset gets it right in
 * both directions:
 *
 *   {milk} ⊂ {amul, milk}            -> "milk" IS the Amul milk
 *   {amul, butter} ⊄ {amul, milk}    -> butter is not milk
 *   {moong, dal} ⊂ {haldiram, moong, dal}
 *
 * It is also why matching is on whole words: "oil" must not find "boiled".
 */
export function isSameThing(a, b) {
    const ka = itemKey(a);
    const kb = itemKey(b);
    if (!ka || !kb)
        return false;
    if (ka === kb)
        return true;
    const wordsA = new Set(ka.split(" ").filter(Boolean));
    const wordsB = new Set(kb.split(" ").filter(Boolean));
    if (wordsA.size === 0 || wordsB.size === 0)
        return false;
    const subset = (small, big) => [...small].every((w) => big.has(w));
    return subset(wordsA, wordsB) || subset(wordsB, wordsA);
}
/** Everything else in `all` that looks like the same thing as `item`. */
export function findSimilar(item, all) {
    return all.filter((other) => other.id !== item.id && isSameThing(item.name, other.name));
}
/**
 * Groups a list into one entry per distinct thing, preserving order.
 *
 * The FIRST member of each group leads it, so a list sorted by urgency stays
 * sorted by urgency: the group appears where its most pressing member was.
 * That matters because the whole list is ordered by what needs attention first.
 */
export function groupByName(items) {
    const groups = [];
    for (const item of items) {
        const existing = groups.find((g) => g.members.some((m) => isSameThing(m.name, item.name)));
        if (existing) {
            existing.members.push(item);
            // The longest name is usually the most specific: prefer "Amul Milk 1L"
            // over the "milk" someone dictated in a hurry.
            if (item.name.length > existing.label.length)
                existing.label = item.name;
        }
        else {
            groups.push({ key: itemKey(item.name), label: item.name, members: [item] });
        }
    }
    return groups;
}
//# sourceMappingURL=similar.js.map